/**
 * An abstract base class for implementing the Singleton pattern.
 * Subclasses should provide concrete implementations for managing a single instance
 * of a value, typically persisting it to storage.
 *
 * @template T The type of the value that the singleton will manage.
 */
export abstract class Singleton<T> {
  /**
   * Abstract method to retrieve the managed value.
   *
   * @returns The managed value of type T.
   */
  public abstract get(): T;
  /**
   * Abstract method to set or update the managed value.
   *
   * @param value The new value of type T to set.
   */
  public abstract set(value: T): void;
  /**
   * Abstract method to clear or reset the managed value.
   */
  public abstract clear(): void;
}

/**
 * A singleton class to manage authentication tokens.
 */
export class AuthSingleton implements Singleton<string> {
  private value: string = '';

  constructor(private key: string) {
    this.value = localStorage.getItem(this.key) || '';
  }

  /**
   * Sets the authentication token.
   *
   * @param token The token to set.
   */
  public set(token: string): void {
    localStorage.setItem(this.key, token);
    this.value = token;
  }

  /**
   * Gets the current authentication token.
   *
   * @returns The authentication token, or an empty string if not set.
   */
  public get(): string {
    // Return type is now just string
    return this.value;
  }

  /**
   * Clears the authentication token.
   */
  public clear(): void {
    localStorage.removeItem(this.key);
    this.value = ''; // Set to empty string
  }
}

export const contains = (str: string, arr: string[]): boolean => arr.some(item => str.includes(item));
